import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Janitor from '../../../modules/Janitor';
import proNotifications from '../../../modules/proNotifications';
import ssPropTypes from '../../../modules/ssPropTypes';

import './NotificationsBadge.scss';

/**
 * Shows a little red dot when there are new notifications for this pro.
 * The notification count will automatically update over time.
 */
export default class NotificationsBadge extends React.PureComponent {
  static propTypes = {
    /* The provider to get the notifications badge for */
    providerId: ssPropTypes.numberLike.isRequired,

    /** (optional) The notifications list to build the badge from.
        If passed, it will not make an API request to the server. */
    notifications: PropTypes.array,

    /** Extra class name(s) to add to the root of the component. */
    className: PropTypes.string,

    /** Hide the badge, if this is true. This uses CSS to hide it, so you can
        use CSS transitions on the hide/show */
    hide: PropTypes.bool,
  };

  static defaultProps = {
    notifications: null,
    className: null,
    hide: false,
  };

  state = {
    unreadCount: 0,
  };

  janitor = new Janitor();

  reloadTimer = null;

  componentDidMount() {
    this.getUnreadCount();
    this.janitor.addStream(proNotifications.notificationsViewedSubject)
      .subscribe(() => this.getUnreadCount());
  }

  componentDidUpdate(prevProps) {
    const lastList = prevProps.notifications;
    if (lastList !== this.props.notifications) {
      this.getUnreadCount();
    }
  }

  componentWillUnmount() {
    this.janitor.cleanup();
  }

  /**
   * Get the number of unread notifications this pro has
   */
  getUnreadCount() {
    const { providerId, notifications } = this.props;
    if (this.reloadTimer) {
      this.janitor.clearTimeout(this.reloadTimer);
    }
    const getNewNotificationCountPromise = () => proNotifications.getNewNotificationCount(
      providerId,
      notifications,
    );
    this.janitor.addPromise(getNewNotificationCountPromise())
      .then(unreadCount => {
        this.setState({ unreadCount });
        // Do it again, if notifications weren't passed in
        if (!notifications) {
          this.reloadTimer = this.janitor.setTimeout(
            () => {
              this.getUnreadCount();
              this.reloadTimer = null;
            },
            proNotifications.refreshTime(),
          );
        }
      });
  }

  render() {
    const { hide } = this.props;
    const { unreadCount } = this.state;
    const shouldShow = !hide && unreadCount > 0;

    return (
      <span
        className={classNames(
          'pro-notification-badge',
          this.props.className,
          { show: shouldShow },
        )}
      >
        <i>
          {(unreadCount > 9) ? '9+' : unreadCount}
        </i>
      </span>
    );
  }
}
